/**
 * 新註冊客戶 onboarding 收尾（掃 QR / 傳單）的真後端 API client。
 * 對應設計：docs/2026-07-10-onboarding-walkin-registration-design.md §3。
 * Phase 1：shopCode→瓦斯行查詢、手機精確比對、上傳、新註冊客戶建立。
 * Phase 2：barcode 查詢（cylinder-by-barcode）、市話+地址精確比對（match/by-landline-address）。
 */
import api from "@services/axios/apiManager";

const BASE = "/app-api/register";

/**
 * walk-in 綁定/建立客戶成功後，重新登入刷新客戶 session。
 * onboarding 期間使用者尚未註冊，開場的 /line/login 會 401 → 前端無 cusJwtToken → 首頁抓不到資料。
 * 綁定後此時 LINE 使用者已關聯客戶，重打一次 /line/login 取得 jwtToken、寫回 sessionStorage 並回傳；
 * 呼叫端再 setCusJwtToken，之後 client 導頁到首頁即可帶新身分抓資料（免整頁重載）。
 */
export async function refreshCustomerSession(accessToken: string): Promise<string | null> {
  if (!accessToken) return null;
  try {
    const res = await api.post<{ data?: { jwtToken?: string } }>(
      "/app-api/line/login",
      { accessToken },
      { isAuth: false },
    );
    const jwt = res.data?.data?.jwtToken;
    if (jwt) sessionStorage.setItem("jwtToken", jwt);
    return jwt || null;
  } catch {
    return null; // 刷新失敗不擋流程；首頁會退回未綁定顯示
  }
}

/**
 * 註冊/綁定成功後刷新身分用：以「新 jwt」重拉 /customer/contexts，供呼叫端重設 chooseIdentity。
 * 不刷新會殘留「舊客戶的 context」→ 之後所有帶 X-Context 的 API 都 403
 * （例：Context 42776 not accessible to customer 155882）。
 */
export const fetchCustomerContexts = (jwt: string) =>
  api.get<{
    status?: string;
    data: {
      contexts: unknown[];
      default_context_id?: number | string | null;
      /** true = 等待安排瓦斯行（註冊完成、無配對、無 CIS）→ 前端全域鎖定等待頁。 */
      waiting_supplier_arrangement?: boolean;
      /** 是否已通過手機 OTP 驗證（後端記於 customer_group_binding）。
       *  false＝結帳頁需補驗；目前唯一為 false 的情境是「掃鋼瓶 barcode 免 OTP 完成註冊」。 */
      phone_verified?: boolean;
    };
  }>("/app-api/customer/contexts", { headers: { Authorization: `Bearer ${jwt}` } });

export interface SupplierByShopCode {
  supplier_id: string;
  supplier_name: string;
  city?: string;
  phone?: string;
  /** 完整營業地址：傳單資訊卡顯示用。後端尚未回傳時為 undefined，前端隱藏該列。 */
  address?: string;
  /** 該瓦斯行是否開放客戶掃鋼瓶 barcode（supplier.system_setting.cylinder_scan，未設定視為 false）。 */
  cylinder_scan?: boolean;
}

/** POST /register/supplier-by-shop-code：shopCode → 瓦斯行公開資訊。查無 → data:null。
 *  改 POST 攜 accessToken：後端 LineUserGuard 只從 body 讀 token（GET 無 body 過不了 guard）。 */
export const fetchSupplierByShopCode = (shopCode: string, accessToken: string) =>
  api.post<{ status?: string; data: SupplierByShopCode | null }>(
    `${BASE}/supplier-by-shop-code`,
    { accessToken, shopCode },
    { isAuth: false },
  );

export interface CylinderByBarcodeSupplier {
  supplier_id: string;
  supplier_name: string;
}

export interface CylinderByBarcodeCustomer {
  cis_id: string;
  phone_masked?: string;
  address_masked?: string;
  has_recent_order?: boolean;
  /** 帶入地址不完整（沒地址／取不出門牌號／拆不出路名）。true → 不顯示「這是您嗎」，直接當新客戶註冊。 */
  address_incomplete?: boolean;
  addresses?: Array<{ address_id: number; address_masked: string; is_represent: boolean }>;
}

export interface CylinderByBarcodeResult {
  state: "matched" | "joined" | "invalid" | "new";
  supplier?: CylinderByBarcodeSupplier;
  customer?: CylinderByBarcodeCustomer;
}

/** POST /register/cylinder-by-barcode：鋼瓶 barcode → 狀態（matched/joined/invalid/new）+ 綁定瓦斯行/客戶（遮罩）。
 *  改 POST 攜 accessToken：後端 LineUserGuard 只從 body 讀 token（GET 無 body 過不了 guard）。 */
export const fetchCylinderByBarcode = (barcode: string, accessToken: string) =>
  api.post<{ status?: string; data: CylinderByBarcodeResult }>(
    `${BASE}/cylinder-by-barcode`,
    { accessToken, barcode },
    { isAuth: false },
  );

export interface MatchByPhoneCustomer {
  cis_id: string;
  address_masked?: string;
  /** 帶入地址不完整（沒地址／取不出門牌號／拆不出路名）。true → 不顯示「這是您嗎」，直接當新客戶註冊。 */
  address_incomplete?: boolean;
  /** 全部配送地址（遮罩）；代表地址 is_represent=true。 */
  addresses?: Array<{ address_id: number; address_masked: string; is_represent: boolean }>;
}

export interface MatchByPhoneResult {
  matched: boolean;
  customer?: MatchByPhoneCustomer;
}

export interface MatchByPhoneBody {
  /** LINE access token：後端 LineUserGuard 從 body 讀取以識別使用者，缺少即 401。 */
  accessToken: string;
  supplier_id: string;
  phone: string;
  /** OTP 驗證票證；未帶時後端只回 matched 布林，不回客戶遮罩資料。 */
  phone_verified_ticket?: string;
}

/** POST /register/match/by-phone：該瓦斯行下手機精確比對。 */
export const matchByPhone = (body: MatchByPhoneBody) =>
  api.post<{ status?: string; data: MatchByPhoneResult }>(`${BASE}/match/by-phone`, body, { isAuth: false });

/** 跨瓦斯行：一支手機在哪些瓦斯行是既有客戶（無指定流程用；需 OTP 票證，只回遮罩）。 */
export interface MatchedSupplierApi {
  supplier_id: string;
  supplier_name: string;
  cis_id: string;
  address_masked?: string;
  /** 僅家用電話（市話）比對的跨行清單會回；手機比對清單不回。 */
  phone_masked?: string;
  /** 瓦斯行聯絡電話／地址：供「找到您所屬的瓦斯行」卡片顯示完整資訊 */
  supplier_phone?: string;
  supplier_address?: string;
  /** 帶入地址不完整（沒地址／取不出門牌號／拆不出路名）。true → 不顯示「這是您嗎」，直接當新客戶註冊。 */
  address_incomplete?: boolean;
  addresses?: Array<{ address_id: number; address_masked: string; is_represent: boolean }>;
}

export interface MatchSuppliersByPhoneResult {
  suppliers: MatchedSupplierApi[];
}

export interface MatchSuppliersByPhoneBody {
  /** LINE access token：後端 LineUserGuard 從 body 讀取以識別使用者，缺少即 401。 */
  accessToken: string;
  phone: string;
  /** OTP 驗證票證（必帶，後端 assertPhoneTicket 驗證）。 */
  phone_verified_ticket: string;
}

/** POST /register/match/suppliers-by-phone：跨瓦斯行以手機找出所屬瓦斯行清單（遮罩）。 */
export const matchSuppliersByPhone = (body: MatchSuppliersByPhoneBody) =>
  api.post<{ status?: string; data: MatchSuppliersByPhoneResult }>(`${BASE}/match/suppliers-by-phone`, body, { isAuth: false });

export interface MatchSuppliersByLandlineAddressBody {
  /** LINE access token：後端 LineUserGuard 從 body 讀取以識別使用者，缺少即 401。 */
  accessToken: string;
  landline: string;
  address: string;
}

/** POST /register/match/suppliers-by-landline-address：跨瓦斯行以市話+地址找出所屬瓦斯行清單（免 OTP、遮罩）。 */
export const matchSuppliersByLandlineAddress = (body: MatchSuppliersByLandlineAddressBody) =>
  api.post<{ status?: string; data: MatchSuppliersByPhoneResult }>(`${BASE}/match/suppliers-by-landline-address`, body, { isAuth: false });

/** 建立「無瓦斯行(無 CIS)」的已註冊客戶：onboarding 沒比對到任何既有客戶時，
 *  仍先建 linebot_user + customer + binding（不綁 supplier），成為已註冊未綁瓦斯行客戶。 */
export interface RegisterUnboundCustomerBody {
  /** LINE access token：後端 LineUserGuard 從 body 讀取以識別使用者。 */
  accessToken: string;
  /** 一般模式必帶（OTP 驗證過的手機）；加帳戶模式（已註冊客戶）可省略——
   *  後端改走「無 ticket＋已註冊 → 用已驗證 main_phone」判定（路徑 C，C1 修法）。 */
  phone?: string;
  /** OTP 驗證票證：一般模式必帶；加帳戶模式可省略（理由同上）。 */
  phone_verified_ticket?: string;
  /** 入口來源（等待名單稽核用）。 */
  entry_source?: "qr" | "flyer";
  /** true = 這次註冊「沒有配對到任何瓦斯行」→ 後端寫等待名單稽核列（平台端 waiting list 用）。 */
  supplier_unmatched?: boolean;
  /** 未配對原因：no_location（未開定位且地址解析不出座標）/ no_supplier_in_area（範圍內查無）。 */
  unmatched_reason?: "no_location" | "no_supplier_in_area";
  /** 入口瓦斯行：有帶則進首頁即建佔位 CIS＋pending_review registration（AW 立即可見）。 */
  supplier_id?: string;
  /** 配送地址整串（onboarding 地址步驟收集）：建佔位 CIS 的代表地址。 */
  address?: string;
  latitude?: number;
  longitude?: number;
  /** 自稱（身分頁按鈕）：true=自稱既有、false=自稱新客戶、省略=中性。寫進佔位 registration。 */
  claimed_existing?: boolean;
  /** 使用者在比對確認卡按「這不是我」／「不是這家」而明確否認的既有客戶 cis_id 清單。
      後端存進 onboarding_registration.rejected_cis_ids，AW 人工核對時把這些候選標成「客戶已否認」。 */
  rejected_cis_ids?: string[];
  /** 客戶實際填寫的市話（原樣，可多支逗號合併）→ 寫進 registration.landline 供 AW 核對。 */
  landline?: string;
}

export const registerUnboundCustomer = (body: RegisterUnboundCustomerBody) =>
  api.post<{ status?: string; data: { customer_id: number; outcome?: "created" | "bound_to_existing" | "already_registered" } }>(
    `${BASE}/unbound-customer`,
    body,
    { isAuth: false },
  );

export interface MatchByLandlineAddressCustomer {
  cis_id: string;
  phone_masked?: string;
  /** 帶入地址不完整（沒地址／取不出門牌號／拆不出路名）。true → 不顯示「這是您嗎」，直接當新客戶註冊。 */
  address_incomplete?: boolean;
}

export interface MatchByLandlineAddressResult {
  matched: boolean;
  customer?: MatchByLandlineAddressCustomer;
}

export interface MatchByLandlineAddressBody {
  /** LINE access token：後端 LineUserGuard 從 body 讀取以識別使用者，缺少即 401。 */
  accessToken: string;
  supplier_id: string;
  landline: string;
  address: string;
  /** 前端 geocode 經緯度，選填，供後端輔助比對。 */
  address_precise?: { lat: number; lng: number };
}

/** POST /register/match/by-landline-address：該瓦斯行下市話+地址精確比對。
 *  已由下方「拆分版」（先比市話 / 比對不到才比地址）取代實際呼叫，此函式暫留供回退用，不刪。 */
export const matchByLandlineAddress = (body: MatchByLandlineAddressBody) =>
  api.post<{ status?: string; data: MatchByLandlineAddressResult }>(`${BASE}/match/by-landline-address`, body, {
    isAuth: false,
  });

/**
 * 拆分版比對（校正版流程）：家用電話(市話)與地址分開比對，不再綁一起——
 * 先用家用電話比對，比對不到才用地址比對。以下 4 支對應後端獨立端點。
 */

export interface MatchByLandlineCustomer {
  cis_id: string;
  address_masked?: string;
  phone_masked?: string;
  /** 帶入地址不完整（沒地址／取不出門牌號／拆不出路名）。true → 不顯示「這是您嗎」，直接當新客戶註冊。 */
  address_incomplete?: boolean;
  addresses?: Array<{ address_id: number; address_masked: string; is_represent: boolean }>;
}

export interface MatchByLandlineResult {
  matched: boolean;
  customer?: MatchByLandlineCustomer;
}

export interface MatchByLandlineBody {
  /** LINE access token：後端 LineUserGuard 從 body 讀取以識別使用者，缺少即 401。 */
  accessToken: string;
  supplier_id: string;
  landline: string;
}

/** POST /register/match/by-landline：該瓦斯行下「家用電話（市話）」精確比對（免 OTP）。 */
export const matchByLandline = (body: MatchByLandlineBody) =>
  api.post<{ status?: string; data: MatchByLandlineResult }>(`${BASE}/match/by-landline`, body, { isAuth: false });

/** 推薦瓦斯行（無指定、比對全落空 → 依座標找附近合作瓦斯行）。 */
export interface RecommendedSupplierApi {
  supplier_id: string;
  supplier_name: string;
  city?: string | null;
  area?: string | null;
  /** 與客戶距離（公里）；未帶座標查詢時為 null。 */
  distance_km?: number | null;
  /** 瓦斯行聯絡電話／地址：供「找到您所屬的瓦斯行」卡片顯示完整資訊 */
  supplier_phone?: string;
  supplier_address?: string;
}

/** POST /register/match/recommend-suppliers：依客戶座標推薦附近合作瓦斯行（haversine 前 5；無座標回未排序清單）。 */
export const recommendSuppliers = (body: { accessToken: string; lat?: number; lng?: number; city?: string; address?: string }) =>
  api.post<{ status?: string; data: { suppliers: RecommendedSupplierApi[] } }>(
    `${BASE}/match/recommend-suppliers`,
    body,
    { isAuth: false },
  );

export interface MatchSuppliersByLandlineBody {
  /** LINE access token：後端 LineUserGuard 從 body 讀取以識別使用者，缺少即 401。 */
  accessToken: string;
  landline: string;
}

/** POST /register/match/suppliers-by-landline：跨瓦斯行以「家用電話（市話）」找出所屬瓦斯行清單（免 OTP、遮罩）。 */
export const matchSuppliersByLandline = (body: MatchSuppliersByLandlineBody) =>
  api.post<{ status?: string; data: MatchSuppliersByPhoneResult }>(`${BASE}/match/suppliers-by-landline`, body, {
    isAuth: false,
  });

export interface MatchByAddressCustomer {
  cis_id: string;
  phone_masked?: string;
  address_masked?: string;
  /** 帶入地址不完整（沒地址／取不出門牌號／拆不出路名）。true → 不顯示「這是您嗎」，直接當新客戶註冊。 */
  address_incomplete?: boolean;
  /** 全部配送地址（遮罩）；代表地址 is_represent=true。 */
  addresses?: Array<{ address_id: number; address_masked: string; is_represent: boolean }>;
}

export interface MatchByAddressResult {
  matched: boolean;
  customer?: MatchByAddressCustomer;
}

export interface MatchByAddressBody {
  /** LINE access token：後端 LineUserGuard 從 body 讀取以識別使用者，缺少即 401。 */
  accessToken: string;
  supplier_id: string;
  address: string;
}

/** POST /register/match/by-address：該瓦斯行下「地址」精確比對（市話比對不到才打，免 OTP）。 */
export const matchByAddress = (body: MatchByAddressBody) =>
  api.post<{ status?: string; data: MatchByAddressResult }>(`${BASE}/match/by-address`, body, { isAuth: false });

export interface MatchedSupplierByAddressApi {
  supplier_id: string;
  supplier_name: string;
  cis_id: string;
  phone_masked?: string;
  /** 瓦斯行聯絡電話／地址：供「找到您所屬的瓦斯行」卡片顯示完整資訊 */
  supplier_phone?: string;
  supplier_address?: string;
  /** 帶入地址不完整（沒地址／取不出門牌號／拆不出路名）。true → 不顯示「這是您嗎」，直接當新客戶註冊。 */
  address_incomplete?: boolean;
}

export interface MatchSuppliersByAddressResult {
  suppliers: MatchedSupplierByAddressApi[];
}

export interface MatchSuppliersByAddressBody {
  /** LINE access token：後端 LineUserGuard 從 body 讀取以識別使用者，缺少即 401。 */
  accessToken: string;
  address: string;
}

/** POST /register/match/suppliers-by-address：跨瓦斯行以「地址」找出所屬瓦斯行清單（市話比對不到才打，免 OTP、遮罩）。 */
export const matchSuppliersByAddress = (body: MatchSuppliersByAddressBody) =>
  api.post<{ status?: string; data: MatchSuppliersByAddressResult }>(`${BASE}/match/suppliers-by-address`, body, {
    isAuth: false,
  });

export interface UploadRegisterFileResult {
  s3_key: string;
}

/** POST /register/upload（multipart）：簽名/鐵牌照上傳，回 S3 object key。
 *  accessToken 走 query string：後端 LineUserGuard 比 multer 先執行，guard 階段 multipart body
 *  尚未解析，body 讀不到 accessToken。若改用自訂 header 則會觸發 CORS preflight（API Gateway 的
 *  OPTIONS MOCK allow-headers 固定、不含自訂 header → 被擋）。改用 query param：multipart POST +
 *  無自訂 header = CORS 簡單請求、不觸發 preflight，guard 直接從 request.query.accessToken 讀。
 *  （另仍放進 form field 供 controller 於 multer 解析後使用。） */
export const uploadRegisterFile = (file: File | Blob, kind: "signature" | "plate", accessToken: string, supplierId?: string) => {
  const formData = new FormData();
  const filename = kind === "signature" ? "signature.png" : "plate.jpg";
  formData.append("file", file, filename);
  formData.append("kind", kind);
  formData.append("accessToken", accessToken);
  if (supplierId) formData.append("supplierId", supplierId);
  const url = `${BASE}/upload?accessToken=${encodeURIComponent(accessToken)}`;
  return api.post<{ status?: string; data: UploadRegisterFileResult }>(url, formData, { isAuth: false });
};

/** 等待安排瓦斯行客戶的完成加入設定暫存：鋼瓶/鐵牌/簽約掛等待列（無 CIS），平台對接時帶入。重送=覆蓋。 */
export const registerWaitingSetup = (body: {
  accessToken: string;
  cylinder: { has: boolean; qty: number; usage: "home" | "biz" | null };
  plates?: { key: string }[];
  signature?: { key: string };
}) =>
  api.post<{ status?: string; data: { registration_id: string } }>(`${BASE}/waiting-setup`, body, { isAuth: false });

/** 查 OTP 重送冷卻剩餘秒數（不發送、不計費）。用於進入 OTP 頁時接續倒數。 */
export const fetchOtpCooldown = (accessToken: string, phone: string) =>
  api.post<{ status?: string; data: { resendAvailableInSeconds: number } }>(
    "/app-api/line/phone-otp-cooldown",
    { accessToken, phone },
    { isAuth: false },
  );

export interface WalkInAddress {
  full_address: string;
  /** 客戶在「這是您嗎」確認卡輸入的門牌號（已 normalizeHouseNo，例：`20`、`20-1`）。
   *  只出現在 customer.address（verifyClaimedCis 驗證輸入）這一側，供後端比對宣稱的 CIS 戶號；
   *  不寫入 customer_address，也不參與 full_address 串接。completed_address 不帶此欄位。
   *  手機/barcode 命中時沒有可帶的完整地址 → full_address 為空字串，驗證只看 house_no。 */
  house_no?: string;
  precise?: { lat: number; lng: number };
  /** 給配送員的指示。後端寫入 customer_address.extra 與 delivery_address.extra；
   *  不會成為地址字串的一部分（兩張表的 full_address 串接式皆不含 extra）。 */
  note?: string;
}

export interface WalkInCustomer {
  /** 命中既有客戶（免填直接綁）時可省略；新客戶（無命中）才需要填。 */
  name?: string;
  /** 選填：拿不到時省略，後端自解（已註冊客戶 main_phone / 票證內號碼）。 */
  phone?: string;
  /** 比對命中、送後端 verifyClaimedCis 驗證用的「單一」市話。
   *  沒比中時絕對不可帶——後端收到就會要求它對得上宣稱的客戶（landlineOk=false 會擋下綁定）。 */
  landline?: string;
  /** 客戶在流程中「實際填寫」的市話（原樣，可多支逗號合併）。不參與驗證，
   *  後端只寫進 onboarding_registration.landline 供 AW 人工核對與候選加權。一律可帶。 */
  landline_input?: string;
  /** 命中既有客戶（免填直接綁）時可省略；新客戶（無命中）才需要填。 */
  address?: WalkInAddress;
  /** 命中且 match 判定地址不完整時，客戶補填的完整地址。只用於綁定時建立 customer_address；
   *  不可與上面的 address（verifyClaimedCis 驗證輸入）混用。 */
  completed_address?: WalkInAddress;
}

export interface WalkInCylinder {
  has: boolean;
  qty: number;
  usage: "home" | "biz" | null;
}

export interface WalkInBody {
  accessToken: string;
  /** 驗證方式二擇一之一：OTP 驗證後取得的票證。命中既有客戶時，與 barcode 至少擇一帶入。 */
  phoneVerifiedTicket?: string;
  /** 驗證方式二擇一之一：掃到的鋼瓶 barcode，供後端比對「barcode 最近持有客戶手機 == 輸入手機」，免 OTP 直接綁定。 */
  barcode?: string;
  /** 二擇一：直接帶已解析的 supplierId，或帶 shopCode 由後端反查。 */
  supplierId?: string;
  shopCode?: string;
  /** §6 100% 命中既有客戶時帶；後端會驗證此筆是否真的比對得上，驗不過會 throw（不會另外撈別筆）。
      isNew=true 時不應帶此欄位。 */
  matchedCisId?: string;
  /** 使用者走完所有比對關卡、最終以「新客戶」身分完成加入設定 → true。
      後端收到 true 時完全不做既有客戶比對（不論 matchedCisId 有沒有帶值），一律視為新客戶建檔。
      「這是我」立即綁定既有客戶的路徑不帶此欄位（或帶 false）。 */
  isNew?: boolean;
  /** 使用者身分頁按鈕的直接自稱（AW 待審清單「身份狀態」欄）：
      true＝有叫過／我是這間客戶（含確認卡「是就是這家」後按不是我）；false＝第一次叫。
      與 isNew 獨立——自稱既有但比對不到本人時仍以 isNew=true 建檔。 */
  claimedExisting?: boolean;
  entrySource?: "qr" | "flyer";
  customer: WalkInCustomer;
  /** 使用者在比對確認卡按「這不是我」／「不是這家」而明確否認的既有客戶 cis_id 清單。
      後端存進 onboarding_registration.rejected_cis_ids，AW 人工核對時把這些候選標成「客戶已否認」。 */
  rejected_cis_ids?: string[];
  /** 命中既有客戶（免填直接綁）時可省略；新客戶才需要填。 */
  cylinder?: WalkInCylinder;
  plates?: { key: string }[];
  signature?: { key: string };
  /** 命中既有客戶（免填直接綁）時可省略；新客戶才需要為 true。 */
  contractAgreed?: boolean;
}

export interface WalkInResult {
  registration_id: string;
  cis_id: string;
  customer_id: string;
  review_status: string;
  supplier_id: string;
}

/** POST /register/walk-in：新註冊客戶建立／綁定既有 CIS（核心）。 */
export const registerWalkIn = (body: WalkInBody) =>
  api.post<{ status?: string; data: WalkInResult }>(`${BASE}/walk-in`, body, { isAuth: false });

/** OA 入口帳戶檢查：已註冊客戶從瓦斯行 OA rich menu 進站時，查在該瓦斯行的帳戶/比對狀態。
 *  只讀不寫；失敗由呼叫端 fail-open。 */
export type EntryCheckStatus = "has_account" | "matched" | "no_match" | "need_otp";

export interface EntryCheckData {
  status: EntryCheckStatus;
  context_id: number | null;
  matched: {
    cis_id: string;
    address_masked: string;
    address_incomplete: boolean;
    addresses: { address_id: number; address_masked: string; is_represent: boolean }[];
  } | null;
}

/** POST /register/entry-check：OA 入口帳戶檢查。 */
export const entryCheck = (params: { accessToken: string; supplier_id: string }) =>
  api.post<{ status?: string; data: EntryCheckData }>(`${BASE}/entry-check`, params, { isAuth: false });

/** SignaturePad 輸出的 PNG dataURL → Blob，供 multipart 上傳。 */
export const dataUrlToBlob = (dataUrl: string): Blob => {
  const [header, base64] = dataUrl.split(",");
  const mimeMatch = /data:(.*?);base64/.exec(header || "");
  const mime = mimeMatch?.[1] || "image/png";
  const binary = atob(base64 || "");
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime });
};

/** 統一從 axios error 取出後端錯誤訊息（中文優先），供 toast 顯示。 */
export const getRegisterApiErrorMessage = (error: unknown): string => {
  if (error && typeof error === "object" && "response" in error) {
    const resp = (error as { response?: { data?: { message?: string } } }).response;
    if (resp?.data?.message) return resp.data.message;
  }
  if (error instanceof Error) return error.message;
  return "發生錯誤，請稍後再試";
};
